import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, Button } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import FavoritesList from './FavoritesList';
import NewFolder from './NewFolder';
import NewItem from './NewItem';
import NewList from './NewList';
import toastr from '../toastr';
import { hasFolder, isFavorite } from './favoriteUtils';
import { modal } from '../modal/ModalEmitter';
import { updateFavorites, updateFavoriteList } from '../../actions/AppActions';

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

const formatGroupLabel = data => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);

const FavoritesContainer = () => {
  const dispatch = useDispatch();
  const { favoriteList } = useSelector(state => state.app.userData);
  const { user } = useSelector(state => state.app);
  const [options, setOptions] = useState([]);

  const canEditList = favoriteList.createdBy === user.id && favoriteList.name !== 'Personal';

  const refreshList = () => {
    ApiService.get('/favorites').then(response => {
      const options = response.data;
      const publicList = [];
      const privateList = [];

      for (const option of options) {
        if (option.isPublic) {
          publicList.push({ value: option.id, label: option.name });
          continue;
        }

        privateList.push({
          value: option.id,
          label: option.name,
        });
      }

      setOptions([
        { label: 'Private', options: privateList },
        { label: 'Public', options: publicList },
      ]);
    });
  };

  const createFolder = payload => {
    const name = payload.folder;
    modal.close();

    const favorites = [...favoriteList.list];

    // Check for existing folder
    if (hasFolder(favorites, name)) {
      toastr.error('Error', `Folder ${name} already exists`);
      return;
    }

    favorites.unshift({ name, items: [`empty-${name}`] });
    dispatch(updateFavorites(favoriteList.id, { list: favorites }));
  };

  const createList = payload => {
    modal.close();
    ApiService.post('/favorites/list', payload).then(response => {
      refreshList();
      dispatch(updateFavoriteList(response.data.id));
    });
  };

  const addItems = payload => {
    // For now don't allow duplicate favorites anywhere in the tree, bugs out
    const items = payload.items.filter(item => !isFavorite(favoriteList.list, item));
    dispatch(updateFavorites(favoriteList.id, { list: [...favoriteList.list, ...items] }));
    modal.close();
  };

  const handleChange = value => {
    dispatch(updateFavoriteList(value.value));
  };

  const handleNewList = () => {
    modal.open('newList', {
      size: 'sm',
      title: 'New List',
      style: 'center',
      hideFooter: true,
      hidePrint: true,
      message: (
        <NewList
          onCancel={modal.close}
          onSubmit={createList}
          listOptions={[...options[0].options, ...options[1].options]}
        />
      ),
    });
  };

  const handleNewFolder = () => {
    modal.open('newFolder', {
      size: 'sm',
      title: 'New Folder',
      style: 'center',
      hideFooter: true,
      hidePrint: true,
      message: <NewFolder onCancel={modal.close} onSubmit={createFolder} />,
    });
  };

  const handleNewFavorite = () => {
    modal.open('newItem', {
      size: 'md',
      title: 'Add Pages',
      style: 'center',
      hideFooter: true,
      hidePrint: true,
      message: <NewItem onCancel={modal.close} onSubmit={addItems} />,
    });
  };

  const handleEditList = () => {
    const handleSubmit = payload => {
      dispatch(updateFavorites(favoriteList.id, { ...payload })).then(() => {
        refreshList();
      });
      modal.close();
    };

    modal.open('newList', {
      size: 'sm',
      title: `Edit List ${favoriteList.name}`,
      style: 'center',
      hideFooter: true,
      hidePrint: true,
      message: (
        <NewList
          onCancel={modal.close}
          onSubmit={handleSubmit}
          listOptions={[...options[0].options, ...options[1].options]}
          initialValue={{ ...favoriteList }}
        />
      ),
    });
  };

  const handleRemoveList = () => {
    modal.confirm({
      message: `Remove the list "${favoriteList.name}"?`,
      callback: () => {
        ApiService.delete(`/favorites/list/${favoriteList.id}`).then(response => {
          refreshList();
          dispatch(updateFavoriteList(response.data.id));
        });
      },
    });
  };

  useEffect(() => {
    refreshList();
  }, []);

  const readOnly = favoriteList.createdBy !== user.id;

  return (
    <div className="favorites">
      <div className="favorites-toolbar">
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          formatGroupLabel={formatGroupLabel}
          onChange={handleChange}
          options={options}
          value={{ value: favoriteList.id, label: favoriteList.name }}
        />
        <Dropdown id="create-options" alignRight>
          <Dropdown.Toggle title="Create" variant="success">
            <i className="zmdi zmdi-plus" /> <span className="hidden-xs hidden-sm">Create</span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item eventKey="3" onClick={handleNewList}>
              List
            </Dropdown.Item>
            <Dropdown.Item eventKey="4" onClick={handleNewFolder} disabled={readOnly}>
              Folder
            </Dropdown.Item>
            <Dropdown.Item eventKey="5" onClick={handleNewFavorite} disabled={readOnly}>
              Favorite
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {favoriteList.isPublic === true && (
        <div className="list-info">
          Created by <span>{favoriteList.createdByName}</span>
        </div>
      )}
      <FavoritesList listId={favoriteList.id} list={[...favoriteList.list]} readOnly={readOnly} />
      {canEditList === true && (
        <div className="vertical-menu">
          <Button title="Delete List" variant="danger" className="vert-item" onClick={handleRemoveList}>
            <i className="zmdi zmdi-delete" />
          </Button>
          <Button title="Edit List" variant="primary" className="vert-item" onClick={handleEditList}>
            <i className="zmdi zmdi-edit" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FavoritesContainer;
